import React, { useState, useEffect } from "react";
import { Sparkles, RefreshCw, ChevronLeft, ChevronDown, ChevronUp, FlaskConical, Code2 } from "lucide-react";
import { AppModal, AppButton, AiModelPicker } from "../ui";
import useAiModels from "../../hooks/useAiModels";
import TagSelector from "./TagSelector";
import apiClient from "../../utils/apiClient";
import { fetchJudgeLanguages, toCategoryOptions } from "../../utils/judgeLanguages";

// ── Class strings ─────────────────────────────────────────────────────────
// CHIP_TONE is a lookup rather than an interpolated class name because Tailwind
// scans source TEXT: a utility name built at runtime emits no CSS at all. The
// difficulty tone has to resolve to a literal string that appears in this file.
const FIELD = "tw-flex tw-min-w-0 tw-flex-col tw-gap-[0.3rem]";
const FIELD_FULL = `${FIELD} tw-col-span-full`;

const LABEL = "tw-text-[0.78rem] tw-font-semibold tw-text-content";
const OPTIONAL = "tw-font-normal tw-text-content-subtle";

const INPUT =
  "tw-w-full tw-rounded-sm tw-border tw-border-solid tw-border-line " +
  "tw-bg-[var(--input-bg)] tw-px-[0.6rem] tw-py-[0.45rem] tw-text-[0.85rem] tw-text-content";

const NOTE_BOX =
  "tw-mt-[0.6rem] tw-block tw-rounded-[0.45rem] tw-bg-surface-sunken " +
  "tw-px-[0.6rem] tw-py-[0.35rem] tw-text-[0.74rem] tw-text-content-muted";

const ERROR_BOX =
  "tw-mt-[0.6rem] tw-rounded-[0.45rem] tw-border tw-border-solid tw-border-danger/30 " +
  "tw-bg-danger/[0.08] tw-px-[0.6rem] tw-py-[0.45rem] tw-text-[0.8rem] tw-text-danger";

const CHIP =
  "tw-inline-flex tw-items-center tw-gap-[0.2rem] tw-rounded-full tw-px-[0.45rem] " +
  "tw-py-[0.1rem] tw-text-[0.66rem] tw-font-semibold";

// The neutral chip is the fallback, and it is what a label chip has always used.
const CHIP_NEUTRAL = `${CHIP} tw-bg-surface-sunken tw-text-content-muted`;

const CHIP_TONE = {
  easy: `${CHIP} tw-bg-success/[0.14] tw-text-success-fg`,
  medium: `${CHIP} tw-bg-warning/[0.16] tw-text-warning-fg`,
  hard: `${CHIP} tw-bg-danger/[0.13] tw-text-danger-fg`,
  lang: `${CHIP} tw-bg-info/[0.12] tw-text-info-fg`,
  tests: `${CHIP} tw-bg-chart-1/[0.12] tw-text-chart-1-fg`,
};

const SECTION_LABEL =
  "tw-mt-[0.3rem] tw-text-[0.68rem] tw-font-bold tw-uppercase tw-tracking-[0.03em] " +
  "tw-text-content-subtle";

// The statement arrives as HTML from the model, so its elements are styled from
// the wrapper — markup we render but do not write.
const DESC =
  "tw-text-[0.82rem] tw-leading-[1.5] tw-text-content " +
  "[&_p]:tw-mb-[0.4rem] [&_p]:tw-mt-0 " +
  "[&_ul]:tw-mb-[0.4rem] [&_ul]:tw-mt-0 " +
  "[&_ol]:tw-mb-[0.4rem] [&_ol]:tw-mt-0 " +
  "[&_pre]:tw-mb-[0.4rem] [&_pre]:tw-mt-0 [&_pre]:tw-overflow-x-auto " +
  "[&_pre]:tw-whitespace-pre-wrap [&_pre]:tw-rounded-[0.4rem] " +
  "[&_pre]:tw-bg-[var(--input-bg)] [&_pre]:tw-px-[0.55rem] [&_pre]:tw-py-[0.4rem]";

const CODE =
  "tw-m-0 tw-overflow-x-auto tw-whitespace-pre tw-rounded-[0.45rem] tw-border " +
  "tw-border-solid tw-border-line tw-bg-[var(--input-bg)] tw-px-[0.6rem] tw-py-2 " +
  "tw-text-[0.74rem] tw-text-content";

const TEST_ROW =
  "tw-flex tw-items-stretch tw-gap-2 tw-rounded-[0.45rem] tw-border tw-border-solid " +
  "tw-border-line tw-bg-surface-raised tw-px-2 tw-py-[0.35rem]";

const TEST_IO =
  "tw-flex tw-min-w-0 tw-flex-1 tw-items-start tw-gap-[0.35rem] " +
  "[&_pre]:tw-m-0 [&_pre]:tw-flex-1 [&_pre]:tw-whitespace-pre-wrap [&_pre]:tw-break-words " +
  "[&_pre]:tw-text-[0.72rem] [&_pre]:tw-text-content";

const TEST_TAG =
  "tw-pt-[0.1rem] tw-text-[0.6rem] tw-font-bold tw-uppercase tw-text-content-subtle";

const MAX_QUESTIONS = 8;
const DIFFICULTIES = ["", "Easy", "Medium", "Hard"];

/**
 * GenerateAiCodingModal — SaaS-admin (platform library) AI authoring of coding
 * questions. The admin gives a topic/summary, difficulty, count, and the target
 * languages; the AI returns full problems (statement HTML, starter + solution
 * code per language, and test cases). Everything is shown for review (uncheck
 * the weak ones, expand to inspect code/test-cases) and the keepers are saved
 * into the bank via the existing /questions/import endpoint.
 *
 * Props: mode ("library" | "institute"), onClose, onSaved(message)
 */
const GenerateAiCodingModal = ({ mode = "library", onClose, onSaved }) => {
  const isLibrary = mode === "library";
  const [langOptions, setLangOptions] = useState([]);
  // A coding problem is statement + starter code + a reference solution + test
  // cases that must actually pass — the heaviest structured-JSON job in the
  // product, and the one where a small model produces the most convincing
  // rubbish. Both roles pick from the shared catalogue.
  const ai = useAiModels("/questions/ai-models");
  const { models, quota, enabled: aiEnabled } = ai;
  const [form, setForm] = useState({
    summary: "",
    difficulty: "",
    count: 3,
    languages: ["JavaScript"],
    labels: "",
    isPublic: isLibrary,
  });
  const [step, setStep] = useState("form"); // form | review
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState({ questions: [], model: "" });
  const [picked, setPicked] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    let alive = true;
    fetchJudgeLanguages()
      .then((raw) => alive && setLangOptions(toCategoryOptions(raw)))
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const quotaBlockedMsg =
    quota && quota.allowed === false
      ? quota.reason === "quota_exhausted"
        ? "Your institute has used all its AI tokens for this month."
        : "AI generation is not available on your current plan."
      : "";

  // Map selected language names → [{id,name}] (categories shape the BE expects).
  const selectedLangObjects = () =>
    form.languages.map((name) => {
      const opt = langOptions.find((o) => o.value === name || o.label === name);
      return { id: opt?.id, name };
    });

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!form.languages.length) {
      setError("Pick at least one language.");
      return;
    }
    setGenerating(true);
    setError("");
    try {
      const { data } = await apiClient.post("/questions/generate-ai", {
        summary: form.summary.trim(),
        difficulty: form.difficulty || undefined,
        count: Math.min(Math.max(Number(form.count) || 3, 1), MAX_QUESTIONS),
        languages: selectedLangObjects(),
        labels: form.labels.split(",").map((l) => l.trim()).filter(Boolean),
        ...ai.payload,
      });
      // `data.model` is what RAN, not what was picked.
      setResult({ questions: data.questions || [], model: data.model || ai.model });
      setPicked(new Set((data.questions || []).map((_, i) => i)));
      setExpanded(new Set());
      setStep("review");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to generate questions.");
    }
    setGenerating(false);
  };

  const togglePick = (i) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const toggleExpand = (i) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const handleSave = async () => {
    const keep = result.questions.filter((_, i) => picked.has(i));
    if (!keep.length) return;
    setSaving(true);
    setError("");
    try {
      const { data } = await apiClient.post("/questions/import", {
        questions: keep.map((q) => ({ ...q, isPublic: isLibrary ? !!form.isPublic : false })),
      });
      const n = data.insertedCount ?? keep.length;
      onSaved(`${n} coding question${n === 1 ? "" : "s"} added${data.errorCount ? `, ${data.errorCount} skipped` : ""}.`);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save questions.");
      setSaving(false);
    }
  };

  const modelLabel = models.find((m) => m.id === result.model)?.label || result.model;

  const footer =
    step === "form" ? (
      <>
        <AppButton variant="outline" type="button" onClick={onClose} disabled={generating}>Cancel</AppButton>
        <AppButton
          variant="default"
          type="button"
          onClick={handleGenerate}
          disabled={generating || !aiEnabled || !models.length || !!quotaBlockedMsg}
        >
          {generating ? (<><RefreshCw size={14} className="tw-animate-spin" /> Generating…</>) : (<><Sparkles size={14} /> Generate</>)}
        </AppButton>
      </>
    ) : (
      <>
        <AppButton variant="outline" type="button" onClick={onClose} disabled={saving}>Discard</AppButton>
        <AppButton variant="default" type="button" onClick={handleSave} disabled={saving || picked.size === 0}>
          {saving ? "Saving…" : `Save ${picked.size} question${picked.size === 1 ? "" : "s"}`}
        </AppButton>
      </>
    );

  return (
    <AppModal
      show
      onHide={() => !generating && !saving && onClose()}
      size="xl"
      icon={<Sparkles size={18} className="tw-align-[-3px] tw-text-chart-1" />}
      title="Generate coding questions with AI"
      footer={footer}
    >
      {step === "form" ? (
        <form onSubmit={handleGenerate} className="tw-flex tw-flex-col tw-gap-3">
          <div className="tw-grid tw-grid-cols-2 tw-gap-3 max-[640px]:tw-grid-cols-1">
            <div className={FIELD_FULL}>
              <label className={LABEL}>
                Topic / summary <span className={OPTIONAL}>(optional)</span>
              </label>
              <textarea
                className={`${INPUT} tw-resize-y`}
                rows={3}
                placeholder="e.g. array & hashmap problems on subarray sums — or leave blank and the AI picks solid interview problems across common patterns"
                value={form.summary}
                onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
                maxLength={1200}
              />
            </div>

            <div className={FIELD}>
              <label className={LABEL}>Difficulty</label>
              <select
                className={INPUT}
                value={form.difficulty}
                onChange={(e) => setForm((f) => ({ ...f, difficulty: e.target.value }))}
              >
                {DIFFICULTIES.map((d, i) => (
                  <option key={d || i} value={d}>{d || "Mixed"}</option>
                ))}
              </select>
            </div>

            <div className={FIELD}>
              <label className={LABEL}>Number of questions</label>
              <input
                type="number"
                min="1"
                max={MAX_QUESTIONS}
                className={INPUT}
                value={form.count}
                onChange={(e) => setForm((f) => ({ ...f, count: e.target.value }))}
                required
              />
            </div>

            <div className={FIELD_FULL}>
              <TagSelector
                options={langOptions}
                value={form.languages}
                onChange={(vals) => setForm((f) => ({ ...f, languages: vals }))}
                label="Languages (starter code + solution generated for each)"
                placeholder="Select languages"
                id="cqai-langs"
              />
            </div>

            <div className={FIELD_FULL}>
              <label className={LABEL}>
                Labels / tags to prefer <span className={OPTIONAL}>(optional, comma-separated)</span>
              </label>
              <input
                className={INPUT}
                placeholder="e.g. Sliding Window, Hashing, Two Pointers"
                value={form.labels}
                onChange={(e) => setForm((f) => ({ ...f, labels: e.target.value }))}
              />
            </div>

            {/* The shared picker lays its own two controls out in a grid, so it
                spans the form grid rather than sitting in one cell. */}
            <AiModelPicker {...ai} className="tw-col-span-full" />

            {isLibrary && (
              <div className={FIELD_FULL}>
                <label className="tw-m-0 tw-flex tw-cursor-pointer tw-items-center tw-gap-[0.45rem] tw-text-[0.82rem] tw-text-content">
                  <input
                    type="checkbox"
                    checked={!!form.isPublic}
                    onChange={(e) => setForm((f) => ({ ...f, isPublic: e.target.checked }))}
                  />
                  <span>Make saved questions public (visible to every institute)</span>
                </label>
              </div>
            )}
          </div>

          {quota && quota.allowed !== false && quota.limit > 0 && (
            <span className={NOTE_BOX}>
              Institute AI tokens: {quota.used.toLocaleString()} / {quota.limit.toLocaleString()} used
              {" · "}{quota.remaining.toLocaleString()} left this month
            </span>
          )}

          <span className="tw-mt-2 tw-block tw-text-[0.76rem] tw-leading-[1.4] tw-text-content-muted">
            The AI writes the full problem statement, starter code and a reference solution for each language,
            plus visible and hidden test cases. Problems already in the bank are skipped. You review everything before it's saved.
          </span>

          {quotaBlockedMsg && <div className={ERROR_BOX}>{quotaBlockedMsg}</div>}
          {error && <div className={ERROR_BOX}>{error}</div>}
        </form>
      ) : (
        <div className="tw-flex tw-flex-col tw-gap-[0.6rem]">
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-[0.6rem] tw-text-[0.8rem] tw-text-content-muted">
            <span>
              {result.questions.length} generated by <strong>{modelLabel}</strong> · {picked.size} selected
            </span>
            <button
              type="button"
              className="tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-[0.15rem] tw-border-none tw-bg-transparent tw-p-0 tw-text-[0.78rem] tw-font-semibold tw-text-primary hover:tw-underline"
              onClick={() => setStep("form")}
            >
              <ChevronLeft size={13} /> Change settings
            </button>
          </div>

          <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
            {result.questions.map((q, i) => {
              const isOpen = expanded.has(i);
              const langs = (q.categories || []).map((c) => (typeof c === "object" ? c.name : c));
              return (
                <li
                  key={i}
                  className={`tw-overflow-hidden tw-rounded-[0.6rem] tw-border tw-border-solid tw-border-line ${
                    picked.has(i) ? "" : "tw-opacity-50"
                  }`}
                >
                  <div className="tw-flex tw-items-start tw-gap-[0.55rem] tw-px-[0.7rem] tw-py-[0.6rem]">
                    <label className="tw-flex tw-items-center tw-pt-[0.15rem]">
                      <input
                        type="checkbox"
                        checked={picked.has(i)}
                        onChange={() => togglePick(i)}
                        aria-label="Keep this question"
                      />
                    </label>
                    <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-[0.35rem]">
                      <div className="tw-text-[0.9rem] tw-font-semibold tw-text-content">{q.title}</div>
                      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-[0.3rem]">
                        <span className={CHIP_TONE[(q.difficulty || "").toLowerCase()] || CHIP_NEUTRAL}>
                          {q.difficulty}
                        </span>
                        {langs.map((l) => (
                          <span key={l} className={CHIP_TONE.lang}><Code2 size={11} /> {l}</span>
                        ))}
                        <span className={CHIP_TONE.tests}><FlaskConical size={11} /> {q.testCases?.length || 0} tests</span>
                        {(q.labels || []).slice(0, 4).map((l) => (
                          <span key={l} className={CHIP_NEUTRAL}>{l}</span>
                        ))}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="tw-inline-flex tw-cursor-pointer tw-rounded-[0.35rem] tw-border-none tw-bg-transparent tw-p-[0.15rem] tw-text-content-muted hover:tw-bg-surface-hover hover:tw-text-content"
                      onClick={() => toggleExpand(i)}
                      aria-label={isOpen ? "Collapse" : "Expand"}
                    >
                      {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  </div>

                  {isOpen && (
                    <div className="tw-flex tw-flex-col tw-gap-[0.35rem] tw-border-0 tw-border-t tw-border-solid tw-border-line tw-bg-surface-alt tw-p-[0.7rem]">
                      <div className={SECTION_LABEL}>Description</div>
                      <div
                        className={DESC}
                        dangerouslySetInnerHTML={{ __html: q.description || "" }}
                      />

                      {langs.map((l) => (
                        q.sampleCode?.[l] ? (
                          <React.Fragment key={l}>
                            <div className={SECTION_LABEL}>Starter code · {l}</div>
                            <pre className={CODE}>{q.sampleCode[l]}</pre>
                          </React.Fragment>
                        ) : null
                      ))}

                      <div className={SECTION_LABEL}>Test cases</div>
                      <div className="tw-flex tw-flex-col tw-gap-[0.35rem]">
                        {(q.testCases || []).map((tc, ti) => (
                          <div className={TEST_ROW} key={ti}>
                            <div className={TEST_IO}>
                              <span className={TEST_TAG}>in</span>
                              <pre>{tc.input}</pre>
                            </div>
                            <div className={TEST_IO}>
                              <span className={TEST_TAG}>out</span>
                              <pre>{tc.expectedOutput}</pre>
                            </div>
                            <span
                              className={`tw-self-center tw-whitespace-nowrap tw-text-[0.64rem] tw-font-semibold ${
                                tc.isHidden ? "tw-text-content-subtle" : "tw-text-success-fg"
                              }`}
                            >
                              {tc.isHidden ? "Hidden" : "Visible"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {error && <div className={ERROR_BOX}>{error}</div>}
        </div>
      )}
    </AppModal>
  );
};

export default GenerateAiCodingModal;
